/**
 * `<caos-status-dot>` — a compact connected/healthy indicator for a service or
 * integration. (Catalog: Status & feedback › StatusDot — a compact connected/healthy indicator for a service or integration.)
 *
 * A small filled dot in a SEMANTIC intent colour (success = connected/healthy,
 * warning = degraded, danger = down, info = informational, muted = unknown/idle),
 * with an optional `label` beside it. The dot uses the saturated semantic hue; the
 * label sits on the page's NEUTRAL surface, so it reads the neutral `--caos-color-text`
 * token (theme-appropriate, ≥4.5:1) — not an on-tint ink, because there is no tint
 * behind the label. An opt-in `pulse` animates the dot for a live signal and is
 * suppressed under `prefers-reduced-motion`. Styling seams: `::part(dot)`,
 * `::part(label)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

export type CaosStatusDotIntent = 'success' | 'warning' | 'danger' | 'info' | 'muted';

const sheet = makeSheet(`
  :host { display: inline-flex; align-items: center; gap: var(--caos-space-2); vertical-align: middle; }

  .dot {
    flex: none;
    width: 9px;
    height: 9px;
    border-radius: var(--caos-radius-pill);
    /* default = muted / unknown */
    background: var(--caos-color-text-muted);
    position: relative;
  }
  :host([intent="success"]) .dot { background: var(--caos-color-success); }
  :host([intent="warning"]) .dot { background: var(--caos-color-warning); }
  :host([intent="danger"]) .dot { background: var(--caos-color-danger); }
  :host([intent="info"]) .dot { background: var(--caos-color-info); }
  :host([intent="muted"]) .dot { background: var(--caos-color-text-muted); }

  /* live pulse — a fading ring in the dot's own colour */
  .dot::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: inherit;
  }
  :host([pulse]) .dot::after { animation: caos-dot-pulse 1.8s ease-out infinite; }
  @keyframes caos-dot-pulse {
    0%   { transform: scale(1); opacity: 0.6; }
    70%  { transform: scale(2.6); opacity: 0; }
    100% { transform: scale(2.6); opacity: 0; }
  }
  @media (prefers-reduced-motion: reduce) {
    :host([pulse]) .dot::after { animation: none; display: none; }
  }

  .label {
    font-family: var(--caos-font);
    font-size: 13px;
    line-height: 1.4;
    color: var(--caos-color-text);
  }
  .label:empty { display: none; }
`);

export class CaosStatusDot extends CaosElement {
  static get observedAttributes(): string[] {
    return ['label', 'intent'];
  }

  private dot: HTMLSpanElement;
  private labelEl: HTMLSpanElement;

  constructor() {
    super(sheet);

    this.dot = document.createElement('span');
    this.dot.className = 'dot';
    this.dot.setAttribute('part', 'dot');
    this.dot.setAttribute('aria-hidden', 'true');

    this.labelEl = document.createElement('span');
    this.labelEl.className = 'label';
    this.labelEl.setAttribute('part', 'label');

    this.root.append(this.dot, this.labelEl);
  }

  connectedCallback(): void {
    // Without a visible label, expose the state name to assistive tech.
    if (!this.hasAttribute('role')) this.setAttribute('role', 'img');
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private sync(): void {
    const label = this.getAttribute('label') ?? '';
    this.labelEl.textContent = label;
    const intent = this.getAttribute('intent') ?? 'muted';
    this.setAttribute('aria-label', label || `Status: ${intent}`);
  }
}

define('caos-status-dot', CaosStatusDot);

declare global {
  interface HTMLElementTagNameMap {
    'caos-status-dot': CaosStatusDot;
  }
}
